import React, {ReactElement} from "react";
import {BaseApp, DIALOG_FLAG_SHOW_CLOSE} from "shared/BaseApp";
import {Button, Card, CircularProgress, Grid, IconButton, Typography} from "@mui/material";
import {NotFoundFragment} from "shared/NotFoundFragment";
import {StyledBoxColumn, StyledBoxRow, StyledContainer, StyledEmpty, StyledSpan} from "shared/StyledComponents";
import {DIVIDER, DW_SM, PAGE_FRAGMENT_WIDTH, PD_LG, PD_XLG, PD_XSM} from "shared/dimens";
import {AVATAR_SM_STYLE} from "shared/constants";
import {MessagesFragment, MessagesFragmentProps, MessagesFragmentState} from "../shared/messages/MessagesFragment";
import {MessagesContextTrait} from "../shared/messages/types";
import {ChatThread, MessageFactory, ParticipantKey, ParticipantType} from "../shared/messages/chat/types";
import {NewAppChat, NewAppChatFragment} from "./NewAppChatFragment";
import {Action} from "../../shared/types";
import {AvatarImage, ContainerWithTitle} from "../util/Components";
import {CHAT_PERSONAS, ChatPersona} from "../../toktiv-common/types";
import {ArrowBackIosNewOutlined, ArrowForwardIosOutlined, MoreVertOutlined} from "@mui/icons-material";
import {BaseAppTabFragment} from "../BaseAppTabFragment";
import {AppAudioCallFragment} from "./AppAudioCallFragment";
import {AppChatFragment} from "./AppChatFragment";
import {AppVideoCallFragment} from "./AppVideoCallFragment";

enum DisplayMode {
  THREADS,
  CHARACTERS,
}

type AppChatsContentFragmentProps = MessagesFragmentProps & {}

type AppChatsContentFragmentState = MessagesFragmentState & {
  threads: ChatThread[],
  personaHighlights: ChatPersona[],
  displayMode: DisplayMode,
  reloading?: boolean,
}

class AppChatsContentFragment extends MessagesFragment<AppChatsContentFragmentProps, AppChatsContentFragmentState> {

  constructor(props: AppChatsContentFragmentProps, context: any) {
    super(props,
      context,
      BaseApp.CONTEXT.getTrait<MessagesContextTrait>().getMyParticipantKey(ParticipantType.MEMBER),
      {
        chat: {
          color: "secondary",
          nestedChatView: true,
          alignment: "auto",
          hasAudioCall: false,
          hasVideoCall: false,
          hasInfo: false,
        },
      },
    );
  }

  protected onCreateState(): AppChatsContentFragmentState {
    const indexes: number[] = [];
    for (let i = 0; i < CHAT_PERSONAS.length; i++) {
      indexes.push(i);
    }
    const highlightIndexes: number[] = [];
    for (let i = 0; i < 3; i++) {
      highlightIndexes.push(indexes.splice(Math.floor(Math.random() * indexes.length), 1)[0]);
    }
    return {
      ...super.onCreateState(),
      displayMode: DisplayMode.THREADS,
      personaHighlights: highlightIndexes.map(n => CHAT_PERSONAS[n]),
    };
  }

  protected onNewChat(persona?: ChatPersona): void {
    if (!persona) {
      persona = CHAT_PERSONAS[Math.floor(Math.random() * CHAT_PERSONAS.length)];
    }
    BaseApp.CONTEXT.showDialog({flags: DIALOG_FLAG_SHOW_CLOSE}, () => <NewAppChatFragment
      persona={persona}
      onCreateNewAppChat={newAppChat => this.onNewAppChat(persona, newAppChat)}
      onCreateNewAppCall={() => this.onNewAppChat(persona)}
    />);
  }

  private async onNewAppChat(persona: ChatPersona, newAppChat?: NewAppChat): Promise<void> {
    BaseApp.CONTEXT.hideDialog();
    this.setState({
      reloading: true,
      displayMode: DisplayMode.THREADS,
    });
    await this.sendChat(persona, newAppChat);
    await this.reload();
    this.setState({
      reloading: false,
    });
  }

  private async sendChat(persona: ChatPersona, newAppChat?: NewAppChat) {
    const from = this.myParticipantKey;
    const to = new ParticipantKey(persona.type, ParticipantType.MEMBER);
    const threads = await this.getChatThreads().getOrLoadListItems();
    const participantKeys = [from, to];
    let thread: ChatThread;
    for (const existingThread of threads) {
      if (existingThread.participantsEquals(participantKeys)) {
        thread = existingThread;
        break;
      }
    }
    if (!thread) {
      thread = ChatThread.createNew(participantKeys);
    }
    thread._setMyParticipantKey(this.myParticipantKey);
    await this.getChatThreads().addListItem(thread);
    if (newAppChat) {
      let message = MessageFactory.createTextMessage(thread.id, from, newAppChat.message);
      await this.getChatMessages(thread.id).addListItem(message);
    }
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<void> {
    if (!this.isMyParticipantKeyValid()) {
      return;
    }
    await this.loadThreads();
  }

  private async loadThreads() {
    const threads = (await this.getChatThreads().getOrLoadListItems())
      .filter(thread => Boolean(thread.participantKeys.find(value => this.myParticipantKey.equals(value))))
      .map(thread => {
        const chatPersona = CHAT_PERSONAS.find(persona => persona.type === thread.participantKeys.find(key => key.id !== this.myParticipantKey.id)?.id);
        thread.displayName = chatPersona?.displayName;
        return thread;
      });
    this.setState({
      threads,
    });
  }

  protected renderContainerContent(): React.ReactElement | null {
    if (!this.isMyParticipantKeyValid()) {
      return <NotFoundFragment path={this.props.path}/>;
    }
    switch (this.state.displayMode) {
      default:
      case DisplayMode.THREADS:
        return this.renderThreads();
      case DisplayMode.CHARACTERS:
        return this.renderCharacters();
    }
  }

  private renderPersona(persona: ChatPersona, span: number, flip?: boolean): ReactElement {
    return <Grid item xs={span}>
      <div style={{background: this.theme.palette.background.default, width: "100%"}}>
        <Button style={{padding: 0, width: "100%", border: DIVIDER}} onClick={() => this.onNewChat(persona)}>
          <StyledBoxColumn style={{width: "100%", padding: PD_LG, alignItems: "center"}}>
            <AvatarImage
              key={persona.type}
              persona={persona}
              flip={flip}
              style={{width: "100%"}}/>
            <Typography variant="h6">{persona.displayName}</Typography>
            <Typography style={{width: "100%", height: "4rem", textWrap: "wrap"}}
                        variant={"caption"}>{persona.title}</Typography>
          </StyledBoxColumn>
        </Button>
      </div>
    </Grid>;
  }

  private renderThreads() {
    return <>
      <ContainerWithTitle title={"Characters"}>
        <Grid container spacing={2}>
          {this.state.personaHighlights.map((persona, index) => {
            return this.renderPersona(persona, 4, index % 2 === 0);
          })}
        </Grid>
        <StyledBoxRow>
          <StyledSpan/>
          <Button onClick={() => this.setState({displayMode: DisplayMode.CHARACTERS})}
                  startIcon={<ArrowForwardIosOutlined/>}>See all characters</Button>
        </StyledBoxRow>
      </ContainerWithTitle>
      <ContainerWithTitle title={"Threads"} accessory={this.state.reloading ? <CircularProgress/> : null}>
        {this.renderChatThreads()}
      </ContainerWithTitle>
    </>;
  }

  private renderCharacters() {
    return <>
      <ContainerWithTitle title={"Characters"}>
        <Grid container spacing={2}>
          {CHAT_PERSONAS.map((persona, index) => {
            return this.renderPersona(persona, 4, index % 2 === 0);
          })}
        </Grid>
        <StyledBoxRow>
          <Button onClick={() => this.setState({displayMode: DisplayMode.THREADS})}
                  startIcon={<ArrowBackIosNewOutlined/>}>Back to threads</Button>
          <StyledSpan/>
        </StyledBoxRow>
      </ContainerWithTitle>
    </>;
  }

  private renderChatThreads(): ReactElement {
    if (!(this.state.threads?.length > 0)) {
      return (
        <StyledEmpty
          imageStyle={{
            width: DW_SM,
          }}
          emptyConfig={{
            title: "No chats found",
            text: "Create a new chat.",
            action: new Action("New Chat", () => this.onNewChat()),
            // iconUrl: chatsImage,
          }}/>
      );
    }

    return <StyledBoxColumn style={{
      width: "100%",
      maxWidth: PAGE_FRAGMENT_WIDTH,
      flexGrow: 1,
      margin: "auto",
      zIndex: 100,
    }}>
      {this.state.threads.map((thread) => {
        const persona = CHAT_PERSONAS.find(persona => thread.participantKeys.map(key => key.id).includes(persona.type));
        if (!persona) {
          return null;
        }
        return (
          <Card
            key={thread.id}
            onClick={() => {
              BaseApp.CONTEXT.showActionsDialog("Talk to " + persona.displayName, [
                new Action("Audio Call", () => {
                  const id = BaseApp.CONTEXT.showFullscreenDialog(() => <AppAudioCallFragment
                    personaType={persona.type}
                    onBackPressed={() => {
                      BaseApp.CONTEXT.hideDialog(id);
                      return true;
                    }}
                    path={this.props.path}
                    myParticipantKey={this.myParticipantKey}
                  />)
                }),
                new Action("Video Call", () => {
                  const id = BaseApp.CONTEXT.showFullscreenDialog(() => <AppVideoCallFragment
                    personaType={persona.type}
                    onBackPressed={() => {
                      BaseApp.CONTEXT.hideDialog(id);
                      return true;
                    }}
                    path={this.props.path}
                    myParticipantKey={this.myParticipantKey}
                  />)
                }),
                new Action("Chat", () => {
                  const id = BaseApp.CONTEXT.showFullscreenDialog(() => <AppChatFragment
                    personaType={persona.type}
                    onBackPressed={() => {
                      BaseApp.CONTEXT.hideDialog(id);
                      return true;
                    }}
                    features={this.features?.chat}
                    path={this.props.path}
                    myParticipantKey={this.myParticipantKey}
                    thread={thread}
                  />)
                }),
              ]);
            }}>
            <Button
              style={{width: "100%", padding: PD_LG, paddingRight: PD_XLG, textAlign: "start"}}>
              <StyledBoxRow style={{alignItems: "center", width: "100%"}}>
                <Card style={AVATAR_SM_STYLE}>
                  <img src={thread.getProfilePhoto()} style={{width: "100%", height: "100%"}}/>
                </Card>
                <StyledBoxColumn style={{gap: PD_XSM, flexBasis: 0, flexGrow: 1}}>
                  <Typography
                    variant={"h6"}>{thread.displayName}</Typography>
                  <Typography>{thread.lastMessage?.text}</Typography>
                </StyledBoxColumn>
                <IconButton
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.showThreadActionsDialog(thread);
                  }}
                >
                  <MoreVertOutlined/>
                </IconButton>
              </StyledBoxRow>
            </Button>
          </Card>
        );
      })}
    </StyledBoxColumn>;
  }

  private showThreadActionsDialog(thread: ChatThread): void {
    BaseApp.CONTEXT.showActionsDialog("Actions", [
      new Action("Delete thread", () => {
        BaseApp.CONTEXT.showConfirmDialog("Are you sure you want to delete this thread? This action cannot be undone.", () => {
          this.deleteThread(thread);
        });
      }).makeDestructive(),
    ]);
  }

  private async deleteThread(thread: ChatThread): Promise<void> {
    BaseApp.CONTEXT.showProgressDialog("Deleting...");
    await this.getChatMessages(thread.id).deleteAllListItems();
    await this.getChatThreads().deleteListItem(thread);
    this.reload();
    BaseApp.CONTEXT.hideProgressDialog();
    BaseApp.CONTEXT.showToast("Thread deleted.");
  }
}

export class AppChatsFragment extends BaseAppTabFragment {

  getTitle(): string {
    return "Chat";
  }

  renderTabContent(): ReactElement {
    return <StyledContainer style={{flexGrow: 1, gap: PD_XLG, padding: PD_LG}}>
      <AppChatsContentFragment path={this.props.path}/>
    </StyledContainer>;
  }
}